import React, { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faWhatsapp } from '@fortawesome/free-brands-svg-icons';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Slider from 'react-slick';
import AOS from 'aos';
import 'aos/dist/aos.css';

import { getLandingConfig } from '../utils/getLandingConfig';
import CalendarComponent from './CalendarComponent';
import BusinessInfoForm from './BusinessInfoForm';
import GoogleReviews from './GoogleReviews';

import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import { db, addDoc, collection, serverTimestamp, logEvent, analytics } from '../firebase';

const LandingPage = () => {
  const [config, setConfig] = useState(null);
  const [showWhatsAppPrompt, setShowWhatsAppPrompt] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [activeGallery, setActiveGallery] = useState(null);

  // 🧠 Registrar visita
  useEffect(() => {
    const sessionIdKey = 'sessionId';
  
    // 🔑 Generar sessionId si no existe
    if (!sessionStorage.getItem(sessionIdKey)) {
      const newSessionId =
        Date.now().toString(36) + Math.random().toString(36).substring(2);
      sessionStorage.setItem(sessionIdKey, newSessionId);
    }
  
    const registerVisit = async () => {
      try {
        const sessionId = sessionStorage.getItem(sessionIdKey);
  
        // Evento en Google Analytics
        if (analytics) logEvent(analytics, 'landing_visit');
  
        // Guardar en Firestore
        await addDoc(collection(db, 'analytics_events'), {
          type: 'visit',
          page: 'landing',
          source: document.referrer || 'direct',
          timestamp: serverTimestamp(),
          sessionId: sessionId || null, // 🧠 este campo es clave
        });
  
        console.log("✅ Visita registrada con sessionId:", sessionId);
      } catch (err) {
        console.error('❌ Error registrando visita:', err);
      }
    };
  
    registerVisit();
  }, []);

  useEffect(() => {
    getLandingConfig().then(setConfig);
  }, []);

  useEffect(() => {
    AOS.init({ once: true });
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => setShowWhatsAppPrompt(true), 3000);
    return () => clearTimeout(timer);
  }, []);

  if (!config) return null;

  const theme = config.theme || {};
  const primary = theme.primary || '#f97316';
  const primaryDark = theme.primaryDark || '#ea580c';
  const accent = theme.accent || '#111827';
  const gradientFrom = theme.gradientFrom || '#fff7ed';
  const gradientTo = theme.gradientTo || '#ffffff';
  const highlight = theme.highlight || '#ffedd5';
  const cardBg = theme.cardBg || '#ffffff';
  const cardBorder = theme.cardBorder || '#f3f4f6';
  // Cuando alguien hace clic en WhatsApp
  const handleWhatsAppClick = async () => {
    try {
      // Registrar evento en Firestore
      await addDoc(collection(db, "analytics_events"), {
        type: "whatsapp_click",
        page: "landing",
        timestamp: new Date().toISOString(),
        sessionId: sessionStorage.getItem('sessionId') || null,
      });
      
  
      // Registrar evento en Google Analytics si lo tienes activo
      if (window.gtag) {
        window.gtag("event", "whatsapp_click", {
          event_category: "engagement",
          event_label: "LandingPage",
        });
      }
  
      console.log("📊 Evento registrado: WhatsApp click");
    } catch (error) {
      console.error("❌ Error al registrar evento:", error);
    }
  };
  

// Cuando alguien envía formulario
const handleFormSubmit = async (formData) => {
  try {
    // Guardar lead en Firestore
    await addDoc(collection(db, "landing_leads"), {
      ...formData,
      page: "landing",
      timestamp: new Date().toISOString(),
    });

    // Enviar evento a Google Analytics
    if (window.gtag) {
      window.gtag("event", "lead_submit", {
        event_category: "conversion",
        event_label: "LandingPage",
      });
    }

    console.log("✅ Lead registrado correctamente");
  } catch (error) {
    console.error("❌ Error registrando lead:", error);
  }
};


  return (
    <>
      <Helmet>
        <title>{config.siteTitle || "Landing Page"}</title>
        <meta name="description" content={config.hero.subtitle} />
        <script async src={`https://www.googletagmanager.com/gtag/js?id=${config.googleTagId}`}></script>
        <script>
          {`
            window.dataLayer = window.dataLayer || [];
            function gtag(){dataLayer.push(arguments);}
            gtag('js', new Date());
            gtag('config', '${config.googleTagId}');
          `}
        </script>
      </Helmet>

      <div className="bg-gray-50 min-h-screen text-gray-800 font-sans">
        {/* Header */}
        <header className="bg-white border-b border-gray-200 py-4 shadow-sm">
  <div className="max-w-7xl mx-auto px-4 flex items-center justify-between">
    {/* Logo */}
    <div className="flex items-center gap-2">
      <img src={config.logo} alt="Logo" className="h-10 object-contain" />
      <span className="font-bold text-xl" style={{ color: accent }}>Soluciones Que Funcionan</span> {/* Opcional */}
    </div>

    {/* Botón de Login */}
    <Link
      to="/login"
      className="text-white px-4 py-2 rounded-md text-sm transition hover:opacity-90"
      style={{ backgroundImage: `linear-gradient(120deg, ${primary}, ${primaryDark})` }}
    >
      {config.login.button}
    </Link>
  </div>
</header>


<section
  className="relative text-center py-24 px-4 text-black overflow-hidden"
  style={{ background: `linear-gradient(135deg, ${gradientFrom}, ${gradientTo})` }}
>
  {/* SVG decorativo superior */}
  <svg
    className="absolute top-0 left-0 w-full h-32"
    preserveAspectRatio="none"
    viewBox="0 0 1440 320"
    style={{ color: highlight }}
  >
    <path
      fill="currentColor"
      d="M0,128L48,122.7C96,117,192,107,288,101.3C384,96,480,96,576,122.7C672,149,768,203,864,208C960,213,1056,171,1152,149.3C1248,128,1344,128,1392,128L1440,128L1440,0L1392,0C1344,0,1248,0,1152,0C1056,0,960,0,864,0C768,0,672,0,576,0C480,0,384,0,288,0C192,0,96,0,48,0L0,0Z"
    />
  </svg>

  <div className="relative z-10">
    <h1
      className="text-4xl md:text-6xl font-extrabold mb-6 drop-shadow-md"
      data-aos="fade-up"
      style={{ color: primary }}
    >
      {config.hero.title}
    </h1>

    <p className="text-xl md:text-2xl mb-8 text-gray-700 max-w-3xl mx-auto" data-aos="fade-up" data-aos-delay="100">
      {config.hero.subtitle}
    </p>

    <ul
  className="mb-10 text-lg text-left max-w-xl mx-auto space-y-4"
  data-aos="fade-up"
  data-aos-delay="200"
>
  {config.hero.bullets.map((b, i) => (
    <li key={i} className="flex items-start gap-3 text-gray-800">
      <span
        className="mt-1 w-3 h-3 flex-shrink-0 rounded-full"
        style={{ backgroundColor: primary }}
      />
      <span>{b}</span>
    </li>
  ))}
</ul>


    <button
      onClick={() => setShowForm(!showForm)}
    className="px-6 py-3 text-white font-semibold rounded-full shadow-md hover:shadow-xl transition transform hover:scale-105 hover:opacity-90"
    style={{ backgroundImage: `linear-gradient(120deg, ${primary}, ${primaryDark})` }}
  >
      {showForm ? config.form.hideText : config.form.showText}
    </button>

    {showForm && (
  <div className="mt-10 max-w-2xl mx-auto" data-aos="fade-up">
    <BusinessInfoForm onSubmit={handleFormSubmit} />  {/* ✅ ahora se usa */}
  </div>
)}

  </div>
</section>

<section className="bg-white py-10 border-t" style={{ borderColor: highlight }}>
  <div className="max-w-6xl mx-auto px-4">
    <h3 className="text-center text-lg sm:text-xl font-semibold text-gray-700 mb-6" data-aos="fade-up">
      Tecnologías que usamos para construir tu sistema
    </h3>

    <div
      className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 gap-6 place-items-center"
      data-aos="fade-up"
      data-aos-delay="100"
    >
      <img
        src="https://cdn.jsdelivr.net/gh/devicons/devicon/icons/react/react-original.svg"
        alt="React"
        className="h-8 sm:h-10 md:h-12 hover:scale-110 transition"
      />
      <img
        src="https://cdn.jsdelivr.net/gh/devicons/devicon/icons/javascript/javascript-original.svg"
        alt="JavaScript"
        className="h-8 sm:h-10 md:h-12 hover:scale-110 transition"
      />
      <img
        src="https://cdn.jsdelivr.net/gh/devicons/devicon/icons/python/python-original.svg"
        alt="Python"
        className="h-8 sm:h-10 md:h-12 hover:scale-110 transition"
      />
      <img
        src="https://cdn.jsdelivr.net/gh/devicons/devicon/icons/firebase/firebase-plain.svg"
        alt="Firebase"
        className="h-8 sm:h-10 md:h-12 hover:scale-110 transition"
      />
      <img
        src="https://cdn.jsdelivr.net/gh/devicons/devicon/icons/flutter/flutter-original.svg"
        alt="Flutter"
        className="h-8 sm:h-10 md:h-12 hover:scale-110 transition"
      />
      <img
        src="https://cdn.jsdelivr.net/gh/devicons/devicon/icons/android/android-original.svg"
        alt="Android"
        className="h-8 sm:h-10 md:h-12 hover:scale-110 transition"
      />
      <img
        src="https://cdn.jsdelivr.net/gh/devicons/devicon/icons/apple/apple-original.svg"
        alt="Apple"
        className="h-8 sm:h-10 md:h-12 hover:scale-110 transition"
      />
      <img
        src="https://cdn.jsdelivr.net/gh/devicons/devicon/icons/google/google-original.svg"
        alt="Google"
        className="h-8 sm:h-10 md:h-12 hover:scale-110 transition"
      />
    </div>
  </div>
</section>



<section
  className="py-20 px-4 text-center border-t border-gray-200"
  style={{ background: `linear-gradient(90deg, ${gradientFrom}, ${gradientTo})` }}
>
  <h2 className="text-3xl font-bold text-gray-900 mb-8" data-aos="fade-up">
    {config.calendar.title}
  </h2>
  <div
    className="max-w-4xl mx-auto border rounded-xl shadow-md p-4"
    data-aos="fade-up"
    data-aos-delay="100"
    style={{ borderColor: cardBorder, backgroundColor: cardBg }}
  >
    <CalendarComponent />
  </div>
</section>

        <section className="py-24 px-4 bg-white" id="services">
  <h2 className="text-4xl font-bold text-center mb-12" data-aos="fade-up" style={{ color: accent }}>
    {config.servicesTitle}
  </h2>
  <div className="grid gap-10 md:grid-cols-2 lg:grid-cols-3 max-w-6xl mx-auto">
    {config.services.map((s, i) => (
      <div
        key={i}
        className="rounded-3xl shadow-md hover:shadow-xl hover:scale-[1.03] transition-all p-6 text-center border"
        style={{ backgroundColor: cardBg, borderColor: cardBorder }}
        data-aos="zoom-in"
        data-aos-delay={i * 100}
      >
        <div className="flex justify-center mb-4">
          <img
            src={s.src}
            alt={s.title}
            className="w-24 h-24 object-contain rounded-full border shadow"
            style={{ borderColor: cardBorder }}
          />
        </div>
        <h3 className="text-xl font-bold mb-2" style={{ color: primary }}>{s.title}</h3>
        <p className="text-gray-600 mb-4">{s.desc}</p>
        {s.gallery?.length ? (
          <button
            onClick={() => setActiveGallery(s.gallery)}
            className="font-medium text-sm hover:underline"
            style={{ color: primary }}
          >
            Ver galería
          </button>
        ) : (
          <p className="text-sm text-gray-400 italic">Sin imágenes</p>
        )}
      </div>
    ))}
  </div>
</section>


        {/* Galería Modal */}
        {activeGallery && (
          <div className="fixed inset-0 bg-black bg-opacity-80 z-50 flex justify-center items-center">
            <div className="bg-white rounded-lg p-4 w-full max-w-3xl relative">
              <button
                className="absolute top-2 right-2 text-gray-600 hover:text-black"
                onClick={() => setActiveGallery(null)}
              >
                ✕
              </button>
              <Slider dots infinite speed={500} slidesToShow={1}>
                {activeGallery.map((img, idx) => (
                  <div key={idx}>
                    <img
                      src={img}
                      alt={`Galería ${idx}`}
                      className="w-full h-[400px] object-contain rounded"
                    />
                  </div>
                ))}
              </Slider>
            </div>
          </div>
        )}

        {/* Reseñas Google */}
        <section className="bg-white py-16">
          <div className="max-w-6xl mx-auto px-4" data-aos="fade-up">
            <GoogleReviews />
          </div>
        </section>

        {showWhatsAppPrompt && (
  <div className="fixed bottom-6 right-6 z-50 w-full max-w-sm px-4 sm:px-0">
    <div className="bg-white rounded-2xl shadow-xl border border-gray-200 p-4 pt-10 relative flex flex-col items-center text-center animate-fade-in-up">
      
      {/* Botón cerrar - Posición segura */}
      <button
        onClick={() => setShowWhatsAppPrompt(false)}
        className="absolute top-2 right-2 text-gray-400 hover:text-red-500 text-xl font-bold"
        aria-label="Cerrar"
        style={{ zIndex: 10 }}
      >
        ×
      </button>

      {/* Avatar */}
      <img
        src="https://i.pravatar.cc/100?img=47"
        alt="Agente de servicio"
        className="w-20 h-20 rounded-full border-2 border-green-500 mb-3"
      />

      {/* Texto */}
      <h3 className="text-base font-semibold text-gray-800">¿Tienes preguntas?</h3>
      <p className="text-sm text-gray-600 mb-3">Comunícate con nosotros</p>

      {/* Botón WhatsApp */}
      <a
        href={config.whatsappModal.link}
        target="_blank"
        rel="noopener noreferrer"
        className="bg-green-600 hover:bg-green-700 text-white text-sm px-4 py-2 rounded-full transition w-full max-w-[200px]"
      >
        {config.whatsappModal.buttonText || "Hablar por WhatsApp"}
      </a>
    </div>
  </div>
)}
<footer className="bg-gray-900 text-gray-200 pt-10 pb-6">
  <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
    
    {/* Secciones principales */}
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-10 mb-10">
      
      {/* Descripción */}
      <div>
        <h2 className="text-2xl font-bold mb-2" style={{ color: primary }}>BBTS</h2>
        <p className="text-sm text-gray-400 leading-relaxed">
          Tu aliado tecnológico en sistemas web, apps, e-commerce y automatización. Soluciones digitales que hacen crecer tu negocio.
        </p>
      </div>

      {/* Soluciones */}
      <div>
        <h3 className="text-lg font-semibold mb-3 text-white">Soluciones</h3>
        <ul className="space-y-2 text-sm">
          {['Sistemas Web', 'E-commerce', 'Apps móviles', 'Automatización'].map((item) => (
            <li key={item}>
              <a href="#services" className="transition hover:opacity-80" style={{ color: primary }}>
                {item}
              </a>
            </li>
          ))}
        </ul>
      </div>

      {/* Redes Sociales */}
      <div>
        <h3 className="text-lg font-semibold mb-3 text-white">Síguenos</h3>
        <div className="flex flex-wrap gap-4 items-center">
          {/* Threads */}
          <a href="https://www.threads.net" target="_blank" rel="noreferrer" aria-label="Threads">
            <img src="https://cdn.jsdelivr.net/gh/devicons/devicon/icons/threads/threads-original.svg" alt="Threads" className="h-6 w-6 hover:scale-110 transition" />
          </a>
          {/* X */}
          <a href="https://x.com" target="_blank" rel="noreferrer" aria-label="X">
            <svg className="h-6 w-6 fill-gray-300 hover:fill-white transition" viewBox="0 0 24 24"><path d="M20.66 3H17.64L12.9 9.04 7.5 3H2.55L9.59 11.18 2 21h3.03l5.34-6.7 5.91 6.7h4.92l-7.26-8.27z"/></svg>
          </a>
          {/* Facebook */}
          <a href="https://facebook.com" target="_blank" rel="noreferrer" aria-label="Facebook">
            <svg className="h-6 w-6 fill-gray-300 hover:fill-white transition" viewBox="0 0 24 24"><path d="M22 12.07C22 6.55 17.52 2 12 2S2 6.55 2 12.07c0 5.04 3.66 9.22 8.44 9.93v-7.03H7.9v-2.9h2.54V9.84c0-2.5 1.49-3.89 3.77-3.89 1.09 0 2.24.2 2.24.2v2.48h-1.26c-1.24 0-1.63.77-1.63 1.56v1.87h2.78l-.44 2.9h-2.34v7.03C18.34 21.29 22 17.11 22 12.07z"/></svg>
          </a>
          {/* YouTube */}
          <a href="https://youtube.com" target="_blank" rel="noreferrer" aria-label="YouTube">
            <svg className="h-6 w-6 fill-gray-300 hover:fill-white transition" viewBox="0 0 24 24"><path d="M21.8 8.001s-.2-1.4-.8-2c-.8-.8-1.7-.8-2.2-.9C16.5 5 12 5 12 5s-4.5 0-6.8.1c-.5.1-1.4.1-2.2.9-.6.6-.8 2-.8 2S2 9.6 2 11.2v1.6c0 1.6.2 3.2.2 3.2s.2 1.4.8 2c.8.8 1.8.8 2.3.9 1.6.2 6.5.2 6.5.2s4.5 0 6.8-.2c.5-.1 1.4-.1 2.2-.9.6-.6.8-2 .8-2s.2-1.6.2-3.2v-1.6c0-1.6-.2-3.2-.2-3.2zM9.75 14.3v-4.6l4.5 2.3-4.5 2.3z"/></svg>
          </a>
          {/* Instagram */}
          <a href="https://instagram.com" target="_blank" rel="noreferrer" aria-label="Instagram">
            <svg className="h-6 w-6 fill-gray-300 hover:fill-white transition" viewBox="0 0 24 24"><path d="M7 2C4.8 2 3 3.8 3 6v12c0 2.2 1.8 4 4 4h10c2.2 0 4-1.8 4-4V6c0-2.2-1.8-4-4-4H7zm10 2c1.1 0 2 .9 2 2v1h-2V4zm-5 3c2.8 0 5 2.2 5 5s-2.2 5-5 5-5-2.2-5-5 2.2-5 5-5zm0 2C9.3 9 8 10.3 8 12s1.3 3 3 3 3-1.3 3-3-1.3-3-3-3z"/></svg>
          </a>
        </div>
      </div>
    </div>

    {/* Divider */}
    <div className="border-t border-gray-700 pt-6 text-center text-sm text-gray-500">
      <div className="mb-2 flex flex-wrap items-center justify-center gap-4">
        <a href="/eula" className="text-gray-300 hover:text-white transition">
          EULA
        </a>
        <a href="/privacy-policy" className="text-gray-300 hover:text-white transition">
          Privacy Policy
        </a>
        <a href="/contact" className="text-gray-300 hover:text-white transition">
          Soporte
        </a>
        <a href="/disclaimers" className="text-gray-300 hover:text-white transition">
          Avisos legales
        </a>
      </div>
      © {new Date().getFullYear()} BBTS. Todos los derechos reservados.
    </div>
  </div>
</footer>



        {/* Botón flotante de WhatsApp */}
<a
  href={config.whatsappFloat.link}
  target="_blank"
  rel="noopener noreferrer"
  onClick={handleWhatsAppClick} // ✅ agregamos esto
  className="fixed bottom-6 left-6 bg-green-600 hover:bg-green-700 text-white p-3 rounded-full shadow-lg z-50"
>
  <FontAwesomeIcon icon={faWhatsapp} size="lg" />
</a>

      </div>
    </>
    
  );
  
};



export default LandingPage;
